import React, { useState, useEffect } from 'react'
import { PlusCircleIcon, TrashIcon, GraduationCapIcon } from 'lucide-react'
import { ApiService } from '../../../api/services.gen'
import { useAlert } from '../../../constants/AlertProvider'

export default function EditEducation({ applicationId }) {
    const [educationList, setEducationList] = useState([])
    const showAlert = useAlert();

    // Fetch existing education
    useEffect(() => {
        const fetchEducation = async () => {
            if (!applicationId) return
            try {
                const res = await ApiService.apiEducationList({ application: applicationId })
                setEducationList(res.results || res)
            } catch (e) {
                showAlert("Failed to load education", "error");
            }
        }
        fetchEducation()
    }, [applicationId])

    // POST: Add new experience
    const addEducation = async () => {
        if (educationList.length >= 2) {
            showAlert("Only 2 education can be added", "error");
            return;
        }
        const newEdu = {
            institution: "Institution name",
            degree: "Degree",
            field_of_study: "Major",
            start_date: "2020-10-10",
            end_date: "2024-10-10",
            application: applicationId,
        }
        try {
            const res = await ApiService.apiEducationCreate({ requestBody: newEdu })
            setEducationList([...educationList, res])
        } catch (error) {
            showAlert("Failed to add education", "error");
            console.log("Failed to add education", error);
        }
    }

    // PATCH: Update education
    const updateEducation = async (id, field, value) => {
        setEducationList(
            educationList.map((edu) =>
                edu.id === id ? { ...edu, [field]: value } : edu
            )
        )
        try {
            const edu = educationList.find((edu) => edu.id === id)
            await ApiService.apiEducationPartialUpdate({
                id,
                requestBody: { ...edu, [field]: value },
            })
        } catch (e) {
            showAlert("Failed to update education", "error");
        }
    }

    // DELETE: Remove education
    const removeEducation = async (id) => {
        setEducationList(educationList.filter((edu) => edu.id !== id))
        try {
            await ApiService.apiEducationDestroy({ id })
        } catch (e) {
            showAlert("Failed to delete education", "error");
        }
    }
    return (
        <div className="space-y-6">
            {educationList.map((education, index) => (
                <div
                    key={education.id}
                    className="pt-4 first:pt-0 border-t first:border-t-0 border-gray-200 dark:border-gray-700"
                >
                    {index > 0 && (
                        <div className="flex justify-end mb-2">
                            <button
                                onClick={() => removeEducation(education.id)}
                                className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex items-center gap-1"
                            >
                                <TrashIcon size={16} />
                                Remove
                            </button>
                        </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label
                                htmlFor={`institution-${education.id}`}
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                School / University
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <GraduationCapIcon size={18} className="text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    id={`institution-${education.id}`}
                                    value={education.institution}
                                    onChange={(e) =>
                                        updateEducation(education.id, 'institution', e.target.value)
                                    }
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                        <div>
                            <label
                                htmlFor={`degree-${education.id}`}
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Degree
                            </label>
                            <input
                                type="text"
                                id={`degree-${education.id}`}
                                value={education.degree}
                                onChange={(e) =>
                                    updateEducation(education.id, 'degree', e.target.value)
                                }
                                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor={`field_of_study-${education.id}`}
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Field of Study
                            </label>
                            <input
                                type="text"
                                id={`field_of_study-${education.id}`}
                                value={education.field_of_study}
                                onChange={(e) =>
                                    updateEducation(education.id, 'field_of_study', e.target.value)
                                }
                                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor={`start_date-${education.id}`}
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Start Date
                            </label>
                            <input
                                type="date"
                                id={`start_date-${education.id}`}
                                value={education.start_date}
                                onChange={(e) =>
                                    updateEducation(education.id, 'start_date', e.target.value)
                                }
                                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor={`end_date-${education.id}`}
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                End Date (or Expected)
                            </label>
                            <input
                                type="date"
                                id={`end_date-${education.id}`}
                                value={education.end_date}
                                onChange={(e) =>
                                    updateEducation(education.id, 'end_date', e.target.value)
                                }
                                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                </div>
            ))}
            <div className="pt-4">
                <button
                    type="button"
                    onClick={addEducation}
                    className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                    <PlusCircleIcon size={20} className="mr-1" />
                    Add Education
                </button>
            </div>
        </div>
    )
}
